import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X, ShieldCheck, AlertTriangle, Pencil, Check, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import { formatTime12, sortDays } from "@/shared/lib/format";
import RouteEditDialog from "@/shared/components/RouteEditDialog";
import { Modal } from "@/shared/motion";

/**
 * ManageSubscriptionDialog — single-screen subscription manage UI for
 * the rider.  Wraps three endpoints:
 *
 *   GET    /api/me/subscription                  – read state
 *   POST   /api/me/subscription/{id}/confirm     – ack next renewal
 *   POST   /api/me/subscription/{id}/cancel      – rider-initiated cancel
 *   PATCH  /api/routes/{route_id}                – edit days/times
 *                                                  (handled by RouteEditDialog;
 *                                                   bumps last_confirmed_at)
 *
 * Backend cron `subscription_renewal_dispatcher.py` enforces the
 * 48-hour rule: if needs_confirmation=true at T-48h and the rider
 * doesn't confirm or edit within 24 hours, Stripe sub is auto-cancelled.
 *
 * The UI surfaces:
 *   · Active route (pickup → destination, days, arrival/return times)
 *   · Next renewal date (from paid_through_date)
 *   · Confirmation banner when needs_confirmation=true
 *   · Edit (opens RouteEditDialog) and Cancel (with confirm) actions
 */
export default function ManageSubscriptionDialog({ open, onClose, onChanged }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { subscription, route }
  const [busy, setBusy] = useState(null);  // 'confirm' | 'cancel' | null
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [editing, setEditing] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/me/subscription");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load subscription");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    refresh();
  }, [open]);

  const sub = data?.subscription || null;
  const route = data?.route || null;

  const renewalLabel = useMemo(() => {
    const iso = sub?.next_renewal;
    if (!iso) return null;
    try {
      const d = new Date(iso);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const target = new Date(d); target.setHours(0, 0, 0, 0);
      const days = Math.round((target - today) / (1000 * 60 * 60 * 24));
      const dateStr = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
      const timeStr = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      let rel = "";
      if (days === 0) rel = " (today)";
      else if (days === 1) rel = " (tomorrow)";
      else if (days > 0 && days <= 7) rel = ` (in ${days} days)`;
      return `${dateStr} · ${timeStr}${rel}`;
    } catch (_) { return null; }
  }, [sub?.next_renewal]);

  const confirmRenewal = async () => {
    if (!sub) return;
    setBusy("confirm");
    try {
      await api.post(`/me/subscription/${sub.id}/confirm`);
      toast.success("Renewal confirmed");
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Confirm failed");
    } finally { setBusy(null); }
  };

  const performCancel = async () => {
    if (!sub) return;
    setBusy("cancel");
    try {
      await api.post(`/me/subscription/${sub.id}/cancel`);
      toast.success("Subscription cancelled");
      setConfirmingCancel(false);
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(null); }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Manage subscription"
      testid="manage-sub-dialog"
      className="bg-white sm:rounded-2xl border hairline shadow-2xl max-h-[92vh] overflow-y-auto sm:max-w-lg"
    >
        <header className="sticky top-0 bg-white border-b hairline px-5 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="label-xs text-[#ed6500]">Subscription</div>
            <h2 className="font-serif text-lg sm:text-xl mt-0.5">Manage</h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={refresh}
              disabled={loading || busy}
              className="p-2 text-[#515E7B] hover:text-[#ed6500] disabled:opacity-50"
              aria-label="Refresh"
              data-testid="manage-sub-refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-[#515E7B] hover:text-[#0A1128]"
              aria-label="Close"
              data-testid="manage-sub-close"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        {loading && !data ? (
          <div className="py-16 flex items-center justify-center" data-testid="manage-sub-loading">
            <Loader2 size={20} className="animate-spin text-[#515E7B]" />
          </div>
        ) : !sub ? (
          <div className="p-8 text-center" data-testid="manage-sub-empty">
            <p className="text-sm text-[#515E7B]">You don't have an active recurring subscription.</p>
          </div>
        ) : (
          <div className="p-5 sm:p-6 space-y-5" data-testid="manage-sub-body">
            {sub.needs_confirmation && (
              <div
                className="border-l-2 border-[#ed6500] bg-[#fff4eb] p-4 flex items-start gap-3"
                data-testid="manage-sub-needs-confirm"
                role="alert"
              >
                <AlertTriangle size={16} className="text-[#ed6500] shrink-0 mt-0.5" />
                <div className="text-xs text-[#0A1128]">
                  <div className="font-semibold mb-1">Action needed before renewal</div>
                  <p className="text-[#515E7B] leading-relaxed">
                    Your subscription renews soon. Confirm to keep it on autopilot, or edit your
                    schedule. Without action, the renewal will auto-cancel to prevent an
                    unintended charge.
                  </p>
                </div>
              </div>
            )}

            {/* Route summary */}
            {route && (
              <section className="space-y-2" data-testid="manage-sub-route">
                <div className="label-xs text-[#515E7B]">Route</div>
                <div className="text-sm text-[#0A1128]">
                  <div className="truncate" data-testid="manage-sub-pickup">{route.pickup_address}</div>
                  <div className="text-[#515E7B] text-xs my-0.5">↓</div>
                  <div className="truncate" data-testid="manage-sub-destination">{route.destination_address}</div>
                </div>
                <div className="text-[11px] text-[#515E7B] mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span data-testid="manage-sub-days">{(sortDays(route.days || []) || []).join(" · ") || "—"}</span>
                  <span>· Arrival {formatTime12(route.arrival_time || "")}</span>
                  <span>· Return {formatTime12(route.return_time || "")}</span>
                </div>
              </section>
            )}

            {/* Renewal */}
            <section className="space-y-2" data-testid="manage-sub-renewal">
              <div className="label-xs text-[#515E7B]">Next renewal</div>
              <div className="text-sm text-[#0A1128]" data-testid="manage-sub-renewal-date">
                {renewalLabel || "—"}
              </div>
              {sub.billing_cycle && (
                <div className="text-[11px] text-[#515E7B] capitalize" data-testid="manage-sub-cycle">
                  {sub.billing_cycle === "payg" ? "One-time" : `${sub.billing_cycle} cycle`}
                </div>
              )}
            </section>

            {/* Actions */}
            <section className="space-y-2 pt-2 border-t hairline">
              {sub.needs_confirmation && (
                <button
                  onClick={confirmRenewal}
                  disabled={!!busy}
                  className="w-full bg-[#ed6500] text-white py-3 text-[10px] tracking-widest uppercase font-semibold hover:bg-[#d65a00] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="manage-sub-confirm-btn"
                >
                  {busy === "confirm" ? <><Loader2 size={14} className="animate-spin" /> Confirming…</> : <><ShieldCheck size={14} /> Confirm renewal</>}
                </button>
              )}
              <button
                onClick={() => setEditing(true)}
                disabled={!!busy}
                className="w-full bg-[#0A1128] text-white py-3 text-[10px] tracking-widest uppercase font-semibold hover:bg-[#1a2348] disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="manage-sub-edit-btn"
              >
                <Pencil size={14} /> Edit schedule
              </button>
              {!confirmingCancel ? (
                <button
                  onClick={() => setConfirmingCancel(true)}
                  disabled={!!busy}
                  className="w-full text-[10px] tracking-widest uppercase font-semibold text-[#ed6500] hover:text-[#d65a00] py-3 disabled:opacity-60 transition-colors"
                  data-testid="manage-sub-cancel-btn"
                >
                  Cancel subscription
                </button>
              ) : (
                <div className="border-l-2 border-[#ed6500] bg-white p-3 space-y-2" data-testid="manage-sub-cancel-confirm">
                  <p className="text-xs text-[#515E7B]">
                    Cancel the subscription and stop all upcoming charges? Trips already paid for in this cycle will run as scheduled.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmingCancel(false)}
                      disabled={!!busy}
                      className="flex-1 text-[10px] tracking-widest uppercase text-[#515E7B] hover:text-[#0A1128] py-2.5 disabled:opacity-50"
                      data-testid="manage-sub-cancel-back"
                    >
                      Keep subscription
                    </button>
                    <button
                      onClick={performCancel}
                      disabled={!!busy}
                      className="flex-1 text-[10px] tracking-widest uppercase font-semibold bg-[#ed6500] text-white py-2.5 hover:bg-[#d65a00] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      data-testid="manage-sub-cancel-confirm-btn"
                    >
                      {busy === "cancel" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Cancel anyway
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

      {/* Edit schedule reuses the existing RouteEditDialog component
          (PATCH /routes/{id}). Backend bumps last_confirmed_at on
          successful edit so the 48h cron treats this as confirmation. */}
      {route && (
        <RouteEditDialog
          open={editing}
          onOpenChange={setEditing}
          route={route}
          onSaved={async () => {
            await refresh();
            onChanged?.();
          }}
        />
      )}
    </Modal>
  );
}
